import { decodeText, sniffImageType, view, type EmbeddedPicture } from './reader';
import type { MediaChapter } from './index';

/**
 * Vorbis comments — the tag format of FLAC, Ogg Vorbis and Opus — and the
 * FLAC picture block that all three use for cover art.
 *
 * Chapters follow the common convention:
 *   CHAPTER001=00:00:00.000
 *   CHAPTER001NAME=The River Bank
 * Cover art is a FLAC picture block: native in FLAC, base64 in a
 * METADATA_BLOCK_PICTURE comment in Ogg (or the older COVERART comment).
 */

const FRONT_COVER = 3;

export interface CommentResult {
  chapters: MediaChapter[];
  picture?: EmbeddedPicture & { type: number };
}

/** Parses a comment block (little-endian lengths, `KEY=value` strings). */
export function parseComments(b: Uint8Array): CommentResult {
  const v = view(b);
  const comments: [string, string][] = [];
  if (b.length >= 8) {
    let at = 4 + v.getUint32(0, true); // vendor string
    const count = at + 4 <= b.length ? v.getUint32(at, true) : 0;
    at += 4;
    for (let i = 0; i < count && at + 4 <= b.length; i++) {
      const length = v.getUint32(at, true);
      at += 4;
      if (at + length > b.length) break;
      const text = decodeText(b.subarray(at, at + length));
      at += length;
      const eq = text.indexOf('=');
      if (eq > 0) comments.push([text.slice(0, eq).toUpperCase(), text.slice(eq + 1)]);
    }
  }

  const times = new Map<string, number>();
  const names = new Map<string, string>();
  const pictures: (EmbeddedPicture & { type: number })[] = [];
  let legacyArt: string | undefined;
  let legacyMime: string | undefined;

  for (const [key, value] of comments) {
    const chapter = /^CHAPTER(\d+)(NAME)?$/.exec(key);
    if (chapter?.[1]) {
      const n = String(Number(chapter[1]));
      if (chapter[2]) names.set(n, value.trim());
      else {
        const seconds = parseTimestamp(value);
        if (seconds !== null) times.set(n, seconds);
      }
    } else if (key === 'METADATA_BLOCK_PICTURE') {
      const picture = parsePictureBlock(fromBase64(value));
      if (picture) pictures.push(picture);
    } else if (key === 'COVERART') {
      legacyArt = value;
    } else if (key === 'COVERARTMIME') {
      legacyMime = value;
    }
  }

  if (pictures.length === 0 && legacyArt) {
    const data = fromBase64(legacyArt);
    if (data.length > 0) pictures.push({ type: FRONT_COVER, data, mimeType: legacyMime || sniffImageType(data) });
  }

  const chapters = [...times.entries()]
    .sort(([a], [b2]) => Number(a) - Number(b2))
    .map(([n, startSec], i) => ({ title: names.get(n) || `Chapter ${i + 1}`, startSec }))
    .sort((a, b2) => a.startSec - b2.startSec);

  const picture = pictures.find((p) => p.type === FRONT_COVER) ?? pictures[0];
  return { chapters, ...(picture ? { picture } : {}) };
}

/** FLAC picture block (big-endian): type, MIME, description, dimensions,
 *  then the image data. */
export function parsePictureBlock(b: Uint8Array): (EmbeddedPicture & { type: number }) | null {
  if (b.length < 32) return null;
  const v = view(b);
  const type = v.getUint32(0);
  const mimeLength = v.getUint32(4);
  let at = 8 + mimeLength;
  if (at + 4 > b.length) return null;
  const mime = decodeText(b.subarray(8, at)).toLowerCase();
  at += 4 + v.getUint32(at); // description
  at += 16; // width, height, colour depth, palette size
  if (at + 4 > b.length) return null;
  const length = v.getUint32(at);
  at += 4;
  const data = b.subarray(at, Math.min(b.length, at + length));
  if (data.length === 0) return null;
  return { type, data, mimeType: mime.startsWith('image/') ? mime : sniffImageType(data) };
}

/** "HH:MM:SS.mmm", "MM:SS" or plain seconds. */
function parseTimestamp(value: string): number | null {
  const parts = value.trim().split(':');
  if (parts.length === 0 || parts.length > 3) return null;
  let seconds = 0;
  for (const part of parts) {
    const n = Number(part);
    if (!Number.isFinite(n) || part === '') return null;
    seconds = seconds * 60 + n;
  }
  return seconds;
}

function fromBase64(text: string): Uint8Array {
  try {
    const raw = atob(text.replace(/\s+/g, ''));
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
  } catch {
    return new Uint8Array(0);
  }
}
